package one.healthium.today.ui

import androidx.compose.runtime.Immutable
import one.healthium.domain.common.FoodUnit
import one.healthium.domain.user.UserModel
import one.healthium.domain.user.models.FoodItemModel
import one.healthium.domain.user.models.FoodLogModel
import one.healthium.insights.domain.models.ContentScreenModel
import one.healthium.loco.asLString
import one.healthium.meal_plans.domain.MealPlanInfoModel
import one.healthium.tasks.domain.DailyTaskModel
import one.healthium.tasks.domain.DailyTaskType
import one.healthium.today.ui.calories_counter.CaloriesCounterUiModel
import one.healthium.today.ui.calories_counter.NutritionGoalUiModel
import one.healthium.today.ui.models.DailyStepsUiModel
import one.healthium.today.ui.models.DailyTaskTypedPayload.LogFood
import one.healthium.today.ui.models.DailyTaskTypedPayload.LogWater
import one.healthium.today.ui.models.DailyTaskTypedPayload.LogWeight
import one.healthium.today.ui.models.DailyTaskTypedPayload.ReadInsight
import one.healthium.today.ui.models.DailyTaskUiModel
import one.healthium.today.ui.models.UpdateWeightUiModel
import one.healthium.today.ui.models.WaterIntakeUiModel
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant


@Immutable
data class TodayUiState(
    val loading: Boolean = false,
    val todayCaloriesCounter: CaloriesCounterUiModel? = null,
    val todayStepsCounter: DailyStepsUiModel? = null,
    val mealPlan: MealPlanInfoModel? = null,
    val foodLogs: List<FoodLogModel>? = null,
    val userModel: UserModel? = null,
    val isMetric: Boolean = false,
    val shouldShowHKBanner: Boolean = false,
    val showPeriodTracker: Boolean = true,
    val dailyTasks: List<DailyTaskModel>? = null,
    val content: ContentScreenModel? = null,
    val latestReadInsightDate: Instant? = null,
    val latestReadMealPlanDate: Instant? = null,
    val latestLogFoodDate: Instant? = null,
) {
    val todayWaterIntake = userModel?.let {
        WaterIntakeUiModel(
            dailyAmount = userModel.dailyWaterIntake,
            currentValue = userModel.todayWaterLog?.value ?: 0f,
            isMetric = isMetric,
        )
    }

    val latestWeightLog = userModel?.let {
        UpdateWeightUiModel(
            currentWeight = userModel.currentWeight ?: 0f,
            date = userModel.latestWeightLog?.date,
            isMetric = isMetric,
        )
    }

    val meals: List<FoodItemModel> = buildList {
        foodLogs?.forEach { foodLog ->
            foodLog.food.forEach {
                add(it)
                if (size == 3) return@buildList
            }
        }
    }.sortedByDescending { it.date }

    val userProfile = userModel?.profile
    val userName = userModel?.profile?.name
    val foodIsEmpty = foodLogs?.isEmpty() == true

    val overallProgress: Float
        get() {
            val caloriesProgress = todayCaloriesCounter?.caloriesGoal?.progress ?: 0f
            val stepsProgress = todayStepsCounter?.progress ?: 0f
            val dailyTasksCompleted = dailyTasks?.count { it.completed } ?: 0
            val dailyTasksGoal = dailyTasks?.size ?: 0
            val dailyTasksProgress = if (dailyTasksGoal > 0) {
                dailyTasksCompleted.toFloat() / dailyTasksGoal
            } else {
                0f
            }

            return (caloriesProgress + stepsProgress + dailyTasksProgress) / 3f
        }

    val dailyTasksUncompleted: List<DailyTaskUiModel>? = dailyTasks
        ?.groupBy { it.type }
        ?.map { (type, tasks) ->
            val typedPayload = when (type) {
                DailyTaskType.LogWater -> LogWater(
                    latestWaterLog = userModel?.latestWaterLog,
                    todayWaterIntake = todayWaterIntake,
                )

                DailyTaskType.LogWeight -> LogWeight(latestWeightLog = latestWeightLog)

                DailyTaskType.LogFood -> LogFood(
                    completed = tasks.count { it.completed },
                    overall = tasks.size,
                    latestFoodLog = meals.firstOrNull(),
                )

                is DailyTaskType.ReadInsight -> ReadInsight(
                    insightsCount = tasks.size,
                    latestReadDate = latestReadInsightDate,
                )

                is DailyTaskType.DoWorkout -> null
                DailyTaskType.LogSymptoms -> null
            }

            DailyTaskUiModel(
                model = tasks.first(),
                typedPayload = typedPayload,
            )
        }

    val latestWaterLog = userModel?.latestWaterLog

    val latestHealthLog = userModel?.latestHealthLog
    val todayLoggedSymptoms = userModel?.todayHealthLog?.answers?.size ?: 0

    companion object {
        val preview = TodayUiState(
            loading = false,
            todayCaloriesCounter = CaloriesCounterUiModel(
                caloriesGoal = NutritionGoalUiModel(goal = 2000f, amount = 1500f),
                carbohydratesGoal = NutritionGoalUiModel(goal = 200f, amount = 150f),
                proteinGoal = NutritionGoalUiModel(goal = 100f, amount = 80f),
                fatGoal = NutritionGoalUiModel(goal = 50f, amount = 40f),
                fiberGoal = NutritionGoalUiModel(goal = 30f, amount = 20f),
            ),
            mealPlan = MealPlanInfoModel(
                id = "1",
                name = ("Classic-2 Big big " +
                        "Meal Plan Almost " +
                        "Carnivore MEAT!").asLString(),
                tags = listOf("lamb", "beef"),
            ),
            foodLogs = listOf(
                FoodLogModel(
                    date = Clock.System.now() - 2.days,
                    food = listOf(
                        FoodItemModel(
                            id = "10",
                            date = Clock.System.now(),
                            amount = 200f,
                            isLiquid = false,
                            name = "Chicken",
                            calories = 0f,
                            fat = 10f,
                            carbohydrates = 20f,
                            protein = 30f,
                            fiber = 5f,
                            tablespoon = 1f,
                            cups = 0.5f,
                            pieces = 2f,
                            portion = 0.5f,
                            unit = FoodUnit.Gram,
                        ),
                        FoodItemModel(
                            id = "1",
                            date = Clock.System.now(),
                            amount = 100f,
                            isLiquid = false,
                            name = "Chicken",
                            calories = 120f,
                            fat = 10f,
                            carbohydrates = 20f,
                            protein = 30f,
                            fiber = 5f,
                            tablespoon = 1f,
                            cups = 0.5f,
                            pieces = 2f,
                            portion = 0.5f,
                            unit = FoodUnit.Gram,
                        ),
                    ),
                ),
                FoodLogModel(
                    date = Clock.System.now() - 1.days,
                    food = listOf(
                        FoodItemModel(
                            id = "10",
                            date = Clock.System.now(),
                            amount = 50f,
                            isLiquid = false,
                            name = "Chicken",
                            calories = 20f,
                            fat = 10f,
                            carbohydrates = 20f,
                            protein = 30f,
                            fiber = 5f,
                            tablespoon = 1f,
                            cups = 0.5f,
                            pieces = 2f,
                            portion = 0.5f,
                            unit = FoodUnit.Gram,
                        ),
                        FoodItemModel(
                            id = "1",
                            date = Clock.System.now(),
                            amount = 100f,
                            isLiquid = false,
                            name = "Chicken",
                            calories = 200f,
                            fat = 10f,
                            carbohydrates = 20f,
                            protein = 30f,
                            fiber = 5f,
                            tablespoon = 1f,
                            cups = 0.5f,
                            pieces = 2f,
                            portion = 0.5f,
                            unit = FoodUnit.Gram,
                        ),
                    ),
                ),
                FoodLogModel(
                    date = Clock.System.now(),
                    food = listOf(
                        FoodItemModel(
                            id = "10",
                            date = Clock.System.now(),
                            amount = 100f,
                            isLiquid = false,
                            name = "Chicken",
                            calories = 120f,
                            fat = 10f,
                            carbohydrates = 20f,
                            protein = 30f,
                            fiber = 5f,
                            tablespoon = 1f,
                            cups = 0.5f,
                            pieces = 2f,
                            portion = 0.5f,
                            unit = FoodUnit.Gram,
                        ),
                        FoodItemModel(
                            id = "1",
                            date = Clock.System.now(),
                            amount = 100f,
                            isLiquid = false,
                            name = "Chicken",
                            calories = 200f,
                            fat = 10f,
                            carbohydrates = 20f,
                            protein = 30f,
                            fiber = 5f,
                            tablespoon = 1f,
                            cups = 0.5f,
                            pieces = 2f,
                            portion = 0.5f,
                            unit = FoodUnit.Gram,
                        ),
                    ),
                )
            ),
            userModel = UserModel.preview,
            isMetric = true,
            todayStepsCounter = DailyStepsUiModel(
                steps = 5000,
                10000,
            ),
        )
    }
}